/* global google */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";

const MapContainer = ({
  onPolygonChange,
  shapesDeleted,
  initialPolygonData = [],
}) => {
  const map = useMap();
  const places = useMapsLibrary("places");

  const [isDrawing, setIsDrawing] = useState(false);

  const polygonRef = useRef(null);
  const drawingPolygonRef = useRef(null);
  const lastLoadedRef = useRef(null);

  // ==================================================
  // POLYGON OPTIONS
  // ==================================================

  const polygonOptions = {
    fillColor: "#ff0000",
    fillOpacity: 0.3,
    strokeColor: "#ff0000",
    strokeWeight: 2,
    editable: true,
    draggable: false,
  };

  // ==================================================
  // EMIT POLYGON CHANGE
  // ==================================================

  const emitPolygonChange = useCallback(
    (polygon) => {
      if (!polygon) {
        onPolygonChange([]);
        return;
      }

      const coords = polygon
        .getPath()
        .getArray()
        .map((p) => ({
          lat: p.lat(),
          lng: p.lng(),
        }));

      onPolygonChange(coords);
    },
    [onPolygonChange]
  );

  // ==================================================
  // ATTACH POLYGON LISTENERS
  // ==================================================

  const attachPolygonListeners = useCallback(
    (polygon) => {
      if (!polygon) return;

      const path = polygon.getPath();

      google.maps.event.addListener(path, "set_at", () => {
        emitPolygonChange(polygon);
      });

      google.maps.event.addListener(path, "insert_at", () => {
        emitPolygonChange(polygon);
      });

      google.maps.event.addListener(path, "remove_at", () => {
        emitPolygonChange(polygon);
      });

      google.maps.event.addListener(polygon, "dragstart", () => {
        map.setOptions({ draggable: false });
      });

      google.maps.event.addListener(polygon, "dragend", () => {
        map.setOptions({ draggable: true });
        emitPolygonChange(polygon);
      });
    },
    [map, emitPolygonChange]
  );

  // ==================================================
  // START DRAWING
  // ==================================================

  const startDrawing = useCallback(() => {
    if (!map) return;

    // Remove existing polygon
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    if (drawingPolygonRef.current) {
      drawingPolygonRef.current.setMap(null);
      drawingPolygonRef.current = null;
    }

    onPolygonChange([]);

    setIsDrawing(true);
  }, [map, onPolygonChange]);

  // ==================================================
  // MAP CLICK - DRAW POLYGON
  // ==================================================

  useEffect(() => {
    if (!map || !isDrawing) return;

    const clickListener = map.addListener("click", (event) => {
      if (!event.latLng) return;

      const point = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      // ================================================
      // FIRST POINT
      // ================================================

      if (!drawingPolygonRef.current) {
        const polygon = new google.maps.Polygon({
          ...polygonOptions,
          map,
          paths: [point],
          editable: false,
          draggable: false,
        });

        drawingPolygonRef.current = polygon;

        return;
      }

      // ================================================
      // ADD NEXT POINT
      // ================================================

      const polygon = drawingPolygonRef.current;

      polygon.getPath().push(
        new google.maps.LatLng(point.lat, point.lng)
      );
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };
  }, [map, isDrawing]);

  // ==================================================
  // FINISH DRAWING WITH DOUBLE CLICK
  // ==================================================

  useEffect(() => {
    if (!map || !isDrawing) return;

    const dblClickListener = map.addListener("dblclick", () => {
      const polygon = drawingPolygonRef.current;

      if (!polygon) return;

      const coordinates = polygon
        .getPath()
        .getArray()
        .map((p) => ({
          lat: p.lat(),
          lng: p.lng(),
        }));

      // Need at least 3 points
      if (coordinates.length < 3) {
        alert("Please select at least 3 points.");

        polygon.setMap(null);
        drawingPolygonRef.current = null;

        return;
      }

      // ================================================
      // DRAWING COMPLETED
      // ================================================

      polygon.setOptions({
        editable: true,
        draggable: false,
      });

      polygonRef.current = polygon;
      drawingPolygonRef.current = null;

      attachPolygonListeners(polygon);

      setIsDrawing(false);

      emitPolygonChange(polygon);
    });

    return () => {
      google.maps.event.removeListener(dblClickListener);
    };
  }, [
    map,
    isDrawing,
    attachPolygonListeners,
    emitPolygonChange,
  ]);

  // ==================================================
  // LOAD EXISTING POLYGON
  // ==================================================

  useEffect(() => {
    if (!map) return;

    const serialized = JSON.stringify(initialPolygonData || []);

    if (serialized === lastLoadedRef.current) {
      return;
    }

    lastLoadedRef.current = serialized;

    // Remove previous polygon
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    if (drawingPolygonRef.current) {
      drawingPolygonRef.current.setMap(null);
      drawingPolygonRef.current = null;
    }

    // No polygon
    if (!initialPolygonData.length) {
      return;
    }

    // Create polygon from existing coordinates
    const polygon = new google.maps.Polygon({
      paths: initialPolygonData,
      map,
      ...polygonOptions,
    });

    polygonRef.current = polygon;

    attachPolygonListeners(polygon);

    // Fit map to polygon
    const bounds = new google.maps.LatLngBounds();

    initialPolygonData.forEach((point) => {
      bounds.extend(point);
    });

    map.fitBounds(bounds);
  }, [
    map,
    initialPolygonData,
    attachPolygonListeners,
  ]);

  // ==================================================
  // DELETE POLYGON
  // ==================================================

  useEffect(() => {
    if (!shapesDeleted) return;

    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    if (drawingPolygonRef.current) {
      drawingPolygonRef.current.setMap(null);
      drawingPolygonRef.current = null;
    }

    lastLoadedRef.current = null;

    setIsDrawing(false);

    onPolygonChange([]);
  }, [shapesDeleted, onPolygonChange]);

  // ==================================================
  // SEARCH AUTOCOMPLETE
  // ==================================================

  useEffect(() => {
    if (!map || !places) return;

    const input = document.getElementById("searchBox");

    if (!input) return;

    const autocomplete = new places.Autocomplete(input);

    autocomplete.bindTo("bounds", map);

    const listener = autocomplete.addListener(
      "place_changed",
      () => {
        const place = autocomplete.getPlace();

        if (!place.geometry) return;

        if (place.geometry.viewport) {
          map.fitBounds(place.geometry.viewport);
        } else {
          map.setCenter(place.geometry.location);
          map.setZoom(15);
        }
      }
    );

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map, places]);

  // ==================================================
  // CLEANUP
  // ==================================================

  useEffect(() => {
    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
      }

      if (drawingPolygonRef.current) {
        drawingPolygonRef.current.setMap(null);
      }
    };
  }, []);

  // ==================================================
  // UI
  // ==================================================

  return (
    <div style={{ position: "relative" }}>

      {/* Search */}

      <input
        id="searchBox"
        placeholder="Search Location"
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 10,
          width: 250,
          height: 40,
          padding: "0 12px",
          borderRadius: "6px",
          border: "1px solid #ccc",
          boxSizing: "border-box",
        }}
      />

      {/* Draw button */}

      <button
        type="button"
        onClick={startDrawing}
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 10,
          height: 40,
          padding: "0 15px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          background: "#fff",
          cursor: "pointer",
        }}
      >
        {isDrawing ? "Drawing..." : "Add Location"}
      </button>

      {/* Instruction */}

      {isDrawing && (
        <div
          style={{
            position: "absolute",
            top: 60,
            left: 10,
            zIndex: 10,
            background: "#fff",
            padding: "8px 12px",
            borderRadius: "6px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            fontSize: "13px",
          }}
        >
          Click on the map to add points.
          <br />
          Double-click to finish.
        </div>
      )}

      <Map
        defaultZoom={14}
        defaultCenter={{
          lat: 11.1271,
          lng: 78.6569,
        }}
        style={{
          width: "100%",
          height: "450px",
          borderRadius: "10px",
        }}
        disableDoubleClickZoom={isDrawing}
      />
    </div>
  );
};

export default MapContainer;